import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls the element matching the current URL hash into view.
 *
 * react-router v6 does not scroll to hash fragments on navigation, so
 * `<Link to="/#pricing">`-style links only update the URL without moving
 * the viewport. This component renders nothing but watches the location
 * hash and performs the scroll itself, both for same-page hash changes
 * and for cross-route navigation that lands on a hash (e.g. from /signin
 * to /#pricing).
 *
 * The target element may not exist yet on first render (e.g. lazy or
 * still-mounting content), so we try immediately and, if not found,
 * retry once on the next animation frame.
 */
export function ScrollToHash() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;

    const id = hash.replace('#', '');
    if (!id) return;

    const scroll = () => {
      const el = document.getElementById(id);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth' });
        return true;
      }
      return false;
    };

    if (!scroll()) {
      const raf = requestAnimationFrame(() => {
        scroll();
      });
      return () => cancelAnimationFrame(raf);
    }

    return undefined;
  }, [hash]);

  return null;
}
